/**
 * GraphQL queries and mutations for the Manage Account page.
 *
 * The account page maps onto the existing user + chip-reseller data model:
 *  - Organization email is the logged-in rescue user's email (read-only).
 *  - Primary Contact / Address / Shipping come from the reseller record and
 *    are only rendered when the user has a chip reseller account.
 */

import { gql } from '@apollo/client';
import type {
  ResellerStatus,
  ResellerTaxExemptType,
  UpdateChipResellerInfoInput,
  UpdateResellerContactInfoInput,
} from '@/types/gql-op-types';
import type { RescueOrgShippingAddress } from './rescueQueries';

/**
 * Everything the Manage Account page renders in one round-trip.
 *
 * Query with `errorPolicy: 'all'` — `chipReseller.shippingAddress` and
 * `contactInfo` have non-null sub-fields, so an empty record on the backend
 * errors the field; the default 'none' policy would null out all of `data`.
 */
export const rescueAccountInfoQuery = gql`
  query rescueAccountInfo {
    currentUser {
      id
      email
      chipReseller {
        id
        name
        verified
        status
        taxExemptType
        contactInfo {
          firstName
          lastName
          primaryEmail
          primaryPhone
          website
          line1
          line2
          city
          state
          zipcode
        }
        shippingAddress {
          line1
          line2
          city
          state
          country
          zip
        }
        preferences {
          registrationEmailOptIn
        }
      }
      userHouseholds {
        household {
          id
          name
          type
        }
      }
    }
  }
`;

export interface RescueAccountContactInfoGql {
  firstName: string;
  lastName?: string | null;
  primaryEmail: string;
  primaryPhone?: string | null;
  website?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
}

export interface RescueAccountInfoData {
  currentUser: {
    id: string;
    email: string;
    chipReseller?: {
      id: string;
      name: string;
      verified: boolean;
      status: ResellerStatus;
      taxExemptType?: ResellerTaxExemptType | null;
      contactInfo?: RescueAccountContactInfoGql | null;
      shippingAddress?: RescueOrgShippingAddress | null;
      preferences: { registrationEmailOptIn: boolean };
    } | null;
    userHouseholds: {
      household: {
        id: string;
        name: string | null;
        type: string;
      };
    }[];
  };
}

/**
 * Save for the Organization / Address / Shipping sections. Each edit modal
 * sends only its own section's fields; the backend merges `contactInfo` into
 * the stored record. `ChipResellerContactInfoInput` covers phone/website/
 * address only — the Primary Contact section (name/phone/email) uses
 * `rescueUpdateResellerContactInfoMutation` below, whose fuller input carries
 * name + email with no backend change.
 *
 * The response selects the full readable contact info so the normalized
 * ChipReseller cache entry stays complete after a save.
 */
export const rescueUpdateAccountInfoMutation = gql`
  mutation rescueUpdateAccountInfo($input: UpdateChipResellerInfoInput!) {
    updateChipResellerInfo(input: $input) {
      id
      name
      taxExemptType
      contactInfo {
        firstName
        lastName
        primaryEmail
        primaryPhone
        website
        line1
        line2
        city
        state
        zipcode
      }
      shippingAddress {
        line1
        line2
        city
        state
        country
        zip
      }
    }
  }
`;

export interface RescueUpdateAccountInfoVariables {
  input: UpdateChipResellerInfoInput;
}

export interface RescueUpdateAccountInfoData {
  updateChipResellerInfo: {
    id: string;
    name: string;
    taxExemptType?: ResellerTaxExemptType | null;
    contactInfo?: RescueAccountContactInfoGql | null;
    shippingAddress?: RescueOrgShippingAddress | null;
  };
}

/**
 * Save for the Primary Contact section. Uses the existing
 * `updateResellerContactInfo` mutation — its `contactInfo` is the full
 * `ChipContactInfoInput` (firstName/lastName/primaryEmail included), so contact
 * name + email persist with **no backend change**. Routes to the same merge
 * service (and same authorization) as `updateChipResellerInfo`.
 */
export const rescueUpdateResellerContactInfoMutation = gql`
  mutation rescueUpdateResellerContactInfo($input: UpdateResellerContactInfoInput!) {
    updateResellerContactInfo(input: $input) {
      id
      name
      taxExemptType
      contactInfo {
        firstName
        lastName
        primaryEmail
        primaryPhone
        website
        line1
        line2
        city
        state
        zipcode
      }
      shippingAddress {
        line1
        line2
        city
        state
        country
        zip
      }
    }
  }
`;

export interface RescueUpdateResellerContactInfoVariables {
  input: UpdateResellerContactInfoInput;
}

export interface RescueUpdateResellerContactInfoData {
  updateResellerContactInfo: {
    id: string;
    name: string;
    taxExemptType?: ResellerTaxExemptType | null;
    contactInfo?: RescueAccountContactInfoGql | null;
    shippingAddress?: RescueOrgShippingAddress | null;
  };
}

/**
 * Rename the rescue household. `adminUpdateHouseholdName` is admin-prefixed, but
 * its policy (`canUpdateHousehold`) also allows a household OWNER — so a rescue
 * owner can rename their own household with **no backend change**. The org-name
 * save fires this alongside `updateChipResellerInfo` so the household name and
 * reseller name stay in sync ("one name, two records").
 */
export const rescueUpdateHouseholdNameMutation = gql`
  mutation rescueUpdateHouseholdName($householdId: ID!, $name: String) {
    adminUpdateHouseholdName(householdId: $householdId, name: $name) {
      id
      name
    }
  }
`;

export interface RescueUpdateHouseholdNameVariables {
  householdId: string;
  name: string;
}

export interface RescueUpdateHouseholdNameData {
  adminUpdateHouseholdName: {
    id: string;
    name: string | null;
  };
}

/** Opt the rescue in or out of the email it gets each time one of its chips is registered. */
export const rescueSetRegistrationEmailOptInMutation = gql`
  mutation rescueSetRegistrationEmailOptIn($optIn: Boolean!) {
    rescueSetRegistrationEmailOptIn(optIn: $optIn) {
      id
      preferences {
        registrationEmailOptIn
      }
    }
  }
`;

export interface RescueSetRegistrationEmailOptInData {
  rescueSetRegistrationEmailOptIn: {
    id: string;
    preferences: { registrationEmailOptIn: boolean };
  };
}
